import type { ProposalProductItemType } from '@econtractor/types';
import * as apiProductSet from 'api/apiProductSets';
import type { DemandProduct } from './types';

const resolveSalePrice = (row: {
    priceSale?: number;
    price?: number;
}): number => {
    const sale = Number(row.priceSale);
    if (Number.isFinite(sale) && sale > 0) return sale;
    const price = Number(row.price);
    if (Number.isFinite(price) && price > 0) return price;
    return Number.isFinite(sale) ? sale : 0;
};

const mergeDemand = (map: Map<number, DemandProduct>, item: DemandProduct) => {
    const existing = map.get(item.productId);
    if (!existing) {
        map.set(item.productId, { ...item });
        return;
    }
    const totalNeed = existing.need + item.need;
    // Середньозважена роздрібна ціна, якщо той самий товар був у КП по різних цінах
    existing.priceSale =
        totalNeed > 0
            ? (existing.need * existing.priceSale + item.need * item.priceSale) / totalNeed
            : item.priceSale;
    existing.need = totalNeed;
    if (!existing.prodName && item.prodName) existing.prodName = item.prodName;
    if (!existing.prodArticle && item.prodArticle) existing.prodArticle = item.prodArticle;
    if (!existing.vendorName && item.vendorName) existing.vendorName = item.vendorName;
    if (!existing.dimensionName && item.dimensionName) existing.dimensionName = item.dimensionName;
};

export interface BuildDemandResult {
    demand: DemandProduct[];
    setErrors: string[];
}

/**
 * Агрегує товари КП по productId. Набори asSetUnit розгортає через getProductSet.
 */
export const buildDemand = async (
    products: ProposalProductItemType[],
): Promise<BuildDemandResult> => {
    const map = new Map<number, DemandProduct>();
    const setErrors: string[] = [];
    const list = Array.isArray(products) ? products : [];

    for (const row of list) {
        const count = Number(row.itemCount || 0);
        if (!(count > 0)) continue;

        const isSetUnit =
            Boolean(row.asSetUnit) ||
            (row.sourceType === 'set' && !(Number(row.productId) > 0));

        if (isSetUnit) {
            const setId = Number(row.sourceId || 0);
            const setName = row.sourceName || row.prodName || `#${setId}`;
            if (!setId) {
                setErrors.push(setName);
                continue;
            }
            const setData = await apiProductSet.getProductSet(setId);
            if (!setData?.items?.length) {
                setErrors.push(setName);
                continue;
            }
            for (const item of setData.items) {
                const productId = Number(item.productId || 0);
                const itemCount = Number(item.itemCount || 0) * count;
                if (!productId || !(itemCount > 0)) continue;
                mergeDemand(map, {
                    productId,
                    need: itemCount,
                    prodName: item.prodName || `#${productId}`,
                    prodArticle: item.prodArticle || '',
                    vendorName: item.vendorName || '',
                    dimensionName: item.dimensionName || '',
                    priceSale: resolveSalePrice({
                        priceSale: item.prodDefPriceSale,
                        price: item.prodDefPrice,
                    }),
                });
            }
            continue;
        }

        const productId = Number(row.productId || 0);
        if (!productId) continue;
        mergeDemand(map, {
            productId,
            need: count,
            prodName: row.prodName || row.sourceName || `#${productId}`,
            prodArticle: row.prodArticle || '',
            vendorName: row.vendorName || '',
            dimensionName: row.dimensionName || '',
            priceSale: resolveSalePrice(row),
        });
    }

    return {
        demand: Array.from(map.values()),
        setErrors,
    };
};
